import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { SignedInGuard } from './core/guards/signed-in.guard';
import { BaseParamsResolverGuard } from './core/guards/base-params-resolver.guard';
import { OpenMobileStoreGuard } from '@app/core/guards/open-mobile-store.guard';
import { RedirectToExternalPageGuard } from './core/guards/redirect-to-external-page.guard';
import { LegalDocumentComponent } from '@app/core/legal-documents/legal-document/legal-document.component';
import { LEGAL_DOCUMENT_FACADE_PROVIDER_TOKEN } from './core/legal-documents/legal-document-provider';
import { TermsFacade } from './core/legal-documents/terms.facade';
import { PrivacyPolicyFacade } from './core/legal-documents/privacy-policy.facade';
import { CookiePolicyFacade } from './core/legal-documents/cookie-policy.facade';
import { ProgramTermsComponent } from '@app/programs/views/terms/program-terms.component';
import { RECEIPT_SCANNER } from '@app/+receipt-scanner/receipt-scanner-consts';
import { signedInOrMemberTokenGuard } from '@app/core/guards/signed-in-or-member-token.guard';
import { MessagesService } from '@app/views/message-preview/messages.service';
import { MEMBERS_ROUTES } from '@app/+member/member-routes';
import { APP_PAGE_PATH, MEMBER_CARD_ROUTE_PATH, MEMBER_PAGE_PATH, PROFILE_ROUTE_PATH } from '@app/consts';
import { GamesService } from '@app/games/services/games.service';
import { AUTH_MODULE_ROOT_PATH } from '@app/login-provider/consts';
import { ForcedProfileUpdateGuard } from '@app/core/guards/force-profile-update/forced-profile-update.guard';
import { REWARDS_ROUTE } from '@app/rewards/rewards-consts';
import { MOBILE_APP_ROUTE } from './mobile-app/mobile-app-consts';
import { PreloadLegalDocumentsResolver } from '@app/core/legal-documents/preload-legal-documents.resolver';
import { ATTENDANCE_CHALLENGE_ROUTE } from '@app/attendance-challenge/attendance-challenge-consts';


const appRoutes: Routes = [
  {
    path: '',
    canActivate: [RedirectToExternalPageGuard, OpenMobileStoreGuard],
    loadChildren: () => import('./+login/login.module').then(m => m.LoginModule),
  },
  {
    path: APP_PAGE_PATH,
    loadComponent: () => import('./views/mobile-app/mobile-app.component').then(m => m.MobileAppComponent),
  },
  {
    path: PROFILE_ROUTE_PATH,
    canActivate: [RedirectToExternalPageGuard],
    loadChildren: () => import('./+profile-form/profile-routes').then(m => m.PROFILE_ROUTES),
  },
  {
    path: 'emails',
    loadChildren: () => import('./emails/routes')
      .then(m => m.EMAIL_ROUTES),
  },
  {
    path: 'messages/:id',
    loadComponent: () => import('./views/message-preview/message-preview.component')
      .then(m => m.MessagePreviewComponent),
    providers: [
      MessagesService,
    ],
  },
  { path: 'msisdns', redirectTo: 'phone' },
  { path: 'sms', redirectTo: 'phone' },
  { path: 'phone', loadChildren: () => import('./+msisdns/msisdns.module').then(m => m.MsisdnsModule) },
  {
    path: MEMBER_PAGE_PATH,
    canActivate: [RedirectToExternalPageGuard, SignedInGuard],
    children: MEMBERS_ROUTES,
  },
  {
    path: MEMBER_CARD_ROUTE_PATH,
    loadComponent: () => import('./+member/member-qr-code-page/member-qr-code-page.component')
      .then(m => m.MemberQrCodePageComponent),
    canActivate: [RedirectToExternalPageGuard, SignedInGuard, ForcedProfileUpdateGuard],
  },
  {
    path: 'export',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'download',
      },
      {
        path: 'download',
        loadComponent: () => import('./views/member-export-download/member-export-download.component')
          .then(m => m.MemberExportDownloadComponent),
      },
    ],
  },
  {
    path: 'offers',
    canActivate: [RedirectToExternalPageGuard, OpenMobileStoreGuard],
    loadChildren: () => import('./+offers/offers.module').then(m => m.OffersModule),
  },
  { path: 'collections', redirectTo: 'offers' },
  {
    path: 'terms',
    component: LegalDocumentComponent,
    providers: [
      {
        provide: LEGAL_DOCUMENT_FACADE_PROVIDER_TOKEN,
        useExisting: TermsFacade,
      },
    ],
    resolve: {
      preloadLegalDocuments: PreloadLegalDocumentsResolver,
    },
  },
  {
    path: 'privacy-policy',
    component: LegalDocumentComponent,
    providers: [
      {
        provide: LEGAL_DOCUMENT_FACADE_PROVIDER_TOKEN,
        useExisting: PrivacyPolicyFacade,
      },
    ],
    resolve: {
      preloadLegalDocuments: PreloadLegalDocumentsResolver,
    },
  },
  {
    path: 'cookie-policy',
    component: LegalDocumentComponent,
    providers: [
      {
        provide: LEGAL_DOCUMENT_FACADE_PROVIDER_TOKEN,
        useExisting: CookiePolicyFacade,
      },
    ],
    resolve: {
      preloadLegalDocuments: PreloadLegalDocumentsResolver,
    },
  },
  {
    path: 'terms/program/:id',
    component: ProgramTermsComponent,
  },
  {
    path: 'game',
    redirectTo: 'games',
  },
  {
    path: 'games',
    canActivate: [RedirectToExternalPageGuard, SignedInGuard],
    providers: [
      GamesService,
    ],
    loadChildren: () => import('@app/games/games-routes')
      .then(m => m.GAMES_ROUTES),
  },
  {
    path: ATTENDANCE_CHALLENGE_ROUTE,
    loadChildren: () => import('@app/attendance-challenge/attendance-challenge.routes')
      .then(m => m.ROUTES),
  },
  {
    path: 'integrations/spaycial',
    canActivate: [RedirectToExternalPageGuard, SignedInGuard],
    loadChildren: () => import('./+spaycial-integration/spaycial-integration.module').then(
      m => m.SpaycialIntegrationModule,
    ),
  },
  {
    path: 'programs',
    loadChildren: () => import('./programs/program.routes')
      .then(r => r.PROGRAM_ROUTES),
  },
  {
    path: RECEIPT_SCANNER,
    canActivate: [signedInOrMemberTokenGuard],
    loadChildren: () => import('./+receipt-scanner/receipt-scanner.module').then(
      m => m.ReceiptScannerModule,
    ),
  },
  {
    path: AUTH_MODULE_ROOT_PATH,
    loadChildren: () => import('@app/login-provider/routes')
      .then(m => m.ROUTES),
  },
  {
    path: REWARDS_ROUTE,
    loadChildren: () => import('@app/rewards/rewards.routes').then(m => m.ROUTES),
  },
  {
    path: MOBILE_APP_ROUTE,
    loadChildren: () => import('@app/mobile-app/mobile-app.routes').then(m => m.ROUTES),
  },
  {
    path: '**',
    canActivate: [RedirectToExternalPageGuard],
    loadComponent: () => import('./views/mobile-app/mobile-app.component').then(m => m.MobileAppComponent),
  },
];

const routes: Routes = [
  { path: '404', loadComponent: () => import('./views/not-found/not-found.component').then(m => m.NotFoundComponent) },
  {
    path: 'q',
    canActivate: [BaseParamsResolverGuard],
    children: [
      {
        path: '404',
        loadComponent: () => import('./views/not-found/not-found.component').then(m => m.NotFoundComponent),
      },
      ...appRoutes,
    ],
  },
  {
    path: '',
    canActivate: [BaseParamsResolverGuard],
    children: appRoutes,
  },
  { path: '**', loadComponent: () => import('./views/not-found/not-found.component').then(m => m.NotFoundComponent) },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      bindToComponentInputs: true,
    }),
  ],
  exports: [RouterModule],
  providers: [SignedInGuard, BaseParamsResolverGuard, RedirectToExternalPageGuard, OpenMobileStoreGuard],
  declarations: [],
})
export class AppRoutingModule {
}
